/**
 * Poker table sound effects.
 *
 * HOW TO CHANGE AN OGG SOUND:
 *   Drop a new .ogg file into packages/ui/src/lib/sounds/ and update the
 *   corresponding import below. The rest of the code stays the same.
 *
 * HOW TO CHANGE A SYNTHESIZED SOUND (win / lowTime):
 *   Edit the playWin() or playLowTick() function below.
 *
 * HOW TO SILENCE ANY SOUND:
 *   Add `return;` at the top of the relevant play* function.
 */

// ─── OGG file imports (Vite resolves these to hashed, cache-busted asset URLs) ─
import checkingUrl from './sounds/checking.ogg';
import foldUrl     from './sounds/fold.ogg';
import makeBetUrl  from './sounds/make-bet.ogg';
import placeCardUrl from './sounds/place-card.ogg';

// Preload all OGG assets on module init so first-play has no latency.
if (typeof window !== 'undefined') {
  for (const url of [checkingUrl, foldUrl, makeBetUrl, placeCardUrl]) {
    const a = new Audio(url);
    a.preload = 'auto';
    a.load();
  }
}

/**
 * Play an OGG URL. Always creates a new HTMLAudioElement so the same sound
 * can overlap itself (e.g. 3 rapid card-deal sounds on the flop).
 * After the first network fetch the browser serves from its HTTP cache.
 */
function playAudioUrl(url: string): void {
  try {
    new Audio(url).play().catch(() => {});
  } catch {
    // ignore — e.g. autoplay policy before first user interaction
  }
}

// ─── OGG-backed sounds ────────────────────────────────────────────────────────

function playCheck(): void  { playAudioUrl(checkingUrl);  }
function playChip(): void   { playAudioUrl(makeBetUrl);   }
function playFold(): void   { playAudioUrl(foldUrl);      }
function playCard(): void   { playAudioUrl(placeCardUrl); }

// ─── Synthesized sounds (Web Audio API) ──────────────────────────────────────

let audioCtx: AudioContext | null = null;

function ctx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  try {
    if (!audioCtx) audioCtx = new AudioContext();
    if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
    return audioCtx;
  } catch {
    return null;
  }
}

/** Ascending chime — win pot */
function playWin(): void {
  const c = ctx();
  if (!c) return;
  const t = c.currentTime;
  // C5, E5, G5, C6
  const freqs = [523, 659, 784, 1047];
  freqs.forEach((freq, i) => {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const start = t + i * 0.11;
    gain.gain.setValueAtTime(0.001, start);
    gain.gain.linearRampToValueAtTime(0.3, start + 0.018);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.55);
    osc.connect(gain);
    gain.connect(c.destination);
    osc.start(start);
    osc.stop(start + 0.6);
  });
}

/** Urgent tick — low time warning (< 5 s remaining on turn) */
function playLowTick(): void {
  const c = ctx();
  if (!c) return;
  const t = c.currentTime;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'square';
  osc.frequency.value = 880;
  gain.gain.setValueAtTime(0.001, t);
  gain.gain.linearRampToValueAtTime(0.18, t + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
  osc.connect(gain);
  gain.connect(c.destination);
  osc.start(t);
  osc.stop(t + 0.1);
}

// ─── Public API ──────────────────────────────────────────────────────────────

export type SoundName = 'check' | 'chip' | 'fold' | 'win' | 'lowTime' | 'placeCard';

export function playSound(name: SoundName): void {
  try {
    switch (name) {
      case 'check':     playCheck();    break;
      case 'chip':      playChip();     break;
      case 'fold':      playFold();     break;
      case 'win':       playWin();      break;
      case 'lowTime':   playLowTick();  break;
      case 'placeCard': playCard();     break;
    }
  } catch {
    // Never let audio errors affect gameplay
  }
}
